import { gql, useQuery } from '@apollo/client';
import type React from 'react';
import MarkersTab from 'src/components/Tabs/MarkersTab/MarkersTab';
import PreviewTabs from 'src/components/Tabs/PreviewTabs/PreviewTabs';
import Tab from 'src/components/Tabs/Tab';
import TabPane from 'src/components/Tabs/TabPane/TabPane';
import TranscriptTab from 'src/components/Tabs/TranscriptTab/TranscriptTab';
import {
  DETAILS_PANEL_VIEW_MODEL_COMP_NAME,
  FD_MIMETYPE_SLUG,
  INFO_PANEL_VIEW_MODEL_COMP_NAME,
  PROXY_FORMAT_SLUG,
  TECH_PANEL_VIEW_MODEL_COMP_NAME,
  VIDEO,
} from 'src/constants';
import { buildFormatsQ } from 'src/dynamoQueries/queryHelpers';
import { getMetadataValue } from 'src/helpers/common';
import { useMetaQ } from 'src/hooks/meta';

interface MarkerData {
  time?: number;
  text?: string;
  duration?: number;
  id?: string;
  markIn?: string;
  markOut?: string;
  title?: string;
  description?: string;
}

interface MediaPageTabsProps {
  clipId: string;
  player: HTMLVideoElement | null;
  defaultTab: string | null;
  edit?: boolean;
  shared?: boolean;
  setMarker: (marker: MarkerData) => void;
}

interface FormatData {
  formats?: Array<{
    id: string;
    [key: string]: unknown;
  }>;
}

const HAS_TRANSCRIPT_QUERY = gql`
  query hasTranscript($assetId: ID!) {
    hasTranscript(assetId: $assetId)
  }
`;

export default function MediaPageTabs({
  clipId,
  player,
  defaultTab,
  edit = false,
  shared = false,
  setMarker,
}: MediaPageTabsProps): React.JSX.Element {
  const { data: proxyFormatData } = useQuery<FormatData>(buildFormatsQ(), {
    skip: !clipId,
    variables: { asset_Id: clipId, fmtType_Slug: PROXY_FORMAT_SLUG },
  });

  const proxyFormat = proxyFormatData?.formats ? proxyFormatData.formats[0] : null;

  const { data: formatData } = useMetaQ(
    {
      skip: !proxyFormat?.id,
      variables: { globalId: proxyFormat?.id, definitionSlugIn: [FD_MIMETYPE_SLUG] },
    },
    shared,
  );

  const mimetype = getMetadataValue(formatData as Record<string, unknown> | null | undefined, FD_MIMETYPE_SLUG);

  const { data: hasTranscriptData } = useQuery<{ hasTranscript: boolean }>(HAS_TRANSCRIPT_QUERY, {
    skip: !clipId || shared,
    variables: { assetId: clipId },
  });

  const hasTranscript = hasTranscriptData?.hasTranscript ?? false;

  return (
    <PreviewTabs defaultTab={defaultTab}>
      <TabPane name="Info" key="1">
        <Tab
          itemId={clipId}
          edit={edit}
          viewModelName={INFO_PANEL_VIEW_MODEL_COMP_NAME}
          isTwoColumn={true}
          shared={shared}
        />
      </TabPane>
      <TabPane name="Details" key="2">
        <Tab
          itemId={clipId}
          edit={edit}
          viewModelName={DETAILS_PANEL_VIEW_MODEL_COMP_NAME}
          isTwoColumn={true}
          shared={shared}
        />
      </TabPane>
      {!shared && (
        <TabPane name="Tech" key="3">
          <Tab itemId={clipId} edit={edit} viewModelName={TECH_PANEL_VIEW_MODEL_COMP_NAME} isTwoColumn={false} />
        </TabPane>
      )}
      {!edit && hasTranscript && !shared && (
        <TabPane name="Transcript" key="5">
          <TranscriptTab player={player as { currentTime?: number | (() => number) }} assetId={clipId} />
        </TabPane>
      )}
      {!edit && mimetype?.includes(VIDEO) && !shared && (
        <TabPane name="Markers" key="4" id="cyp-markers-tab">
          <MarkersTab
            player={player as unknown as { getAttribute: (name: string) => string }}
            clipId={clipId}
            setMarker={setMarker as (marker: MarkerData | Record<string, never>) => void}
          />
        </TabPane>
      )}
    </PreviewTabs>
  );
}
